/**
 * Active-rule filtering.
 *
 * The rules fetched from the API are registered ONLY as SimpleSchema's message
 * catalogue - they do not control whether a predicate fires. The predicates are
 * hardcoded and always run. Suppressing a rule for a spec version therefore
 * means dropping its ERROR after the fact, which is what this module does.
 *
 * FAIL OPEN, always. An empty or absent active set means "we do not know which
 * rules are active" - a failed fetch, a cold store - not "no rules are active".
 * Showing one rule too many is a nuisance; silently suppressing validation on a
 * criminal-justice record is not.
 */
function hasCode(activeCodes, code) {
  if (activeCodes instanceof Set) return activeCodes.has(code);
  return activeCodes.indexOf(code) !== -1;
}

function isEmptySet(activeCodes) {
  if (!activeCodes) return true;
  if (activeCodes instanceof Set) return activeCodes.size === 0;
  return !Array.isArray(activeCodes) || activeCodes.length === 0;
}

// The managed rule namespace - codes the server resolver actually retires by
// spec version. A non-empty errType OUTSIDE this namespace (a SimpleSchema
// built-in, a newly hand-rolled code with no DB row yet, or a state layer the
// server failed to resolve) is exactly as unattributable as a missing errType
// and must pass through the same as the falsy case above. The API deliberately
// chose open-world retirement over closed-world; this keeps the client in sync
// with that choice instead of silently dropping anything outside the four
// managed prefixes.
const MANAGED_NAMESPACE = /^(NIBRS|TEXAS|LIBRS|MSIBRS)-/;

export const isRuleActive = (errType, activeCodes) => {
  if (isEmptySet(activeCodes)) return true; // fail open
  if (!errType) return true; // unattributable - never suppress
  if (!MANAGED_NAMESPACE.test(errType)) return true; // unattributable - outside managed namespace, never suppress
  return hasCode(activeCodes, errType);
};

export const filterErrors = (errors, activeCodes) => {
  if (!Array.isArray(errors)) return [];
  if (isEmptySet(activeCodes)) return errors; // fail open
  return errors.filter((e) => isRuleActive(e && e.errType, activeCodes));
};
